"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ActorOptimizeQueryOperationGroupFileSources = void 0;
const bus_optimize_query_operation_1 = require("@comunica/bus-optimize-query-operation");
const context_entries_1 = require("@comunica/context-entries");
const core_1 = require("@comunica/core");
/**
 * A comunica Group File Sources Optimize Query Operation Actor.
 *
 * Optimizes federated queries over file sources by combining multiple
 * file sources (those identified with forceSourceType 'file') into a single 'compositefile' source.
 * This actor runs after source identification and before source skolemization.
 */
class ActorOptimizeQueryOperationGroupFileSources extends bus_optimize_query_operation_1.ActorOptimizeQueryOperation {
    static updateOperationTypes = new Set([
        'compositeupdate',
        'deleteinsert',
        'load',
        'clear',
        'create',
        'drop',
        'add',
        'move',
        'copy',
    ]);
    mediatorQuerySourceIdentify;
    constructor(args) {
        super(args);
        this.mediatorQuerySourceIdentify = args.mediatorQuerySourceIdentify;
    }
    async test(action) {
        if (action.context.get(context_entries_1.KeysQuerySourceIdentify.traverse)) {
            return (0, core_1.failTest)(`Actor ${this.name} does not work in traversal mode.`);
        }
        if (ActorOptimizeQueryOperationGroupFileSources.updateOperationTypes.has(action.operation.type)) {
            return (0, core_1.failTest)(`Actor ${this.name} does not work for SPARQL Update operations.`);
        }
        return (0, core_1.passTestVoid)();
    }
    async run(action) {
        const querySources = action.context.get(context_entries_1.KeysQueryOperation.querySources) ?? [];
        // Only optimize when there are at least 2 sources
        if (querySources.length < 2) {
            return { operation: action.operation, context: action.context };
        }
        // Identify file sources
        const fileSources = (await Promise.all(querySources
            .map(async (wrapper) => ({
            wrapper,
            filterFactor: await this.getFilterFactorSafe(wrapper, action.context),
        }))))
            .filter(entry => entry.filterFactor === 0)
            .map(entry => entry.wrapper);
        // Only optimize when there are at least 2 file sources
        if (fileSources.length < 2) {
            return { operation: action.operation, context: action.context };
        }
        // Create a single compositefile source via the mediator
        const { querySource: compositeWrapper } = await this.mediatorQuerySourceIdentify.mediate({
            querySourceUnidentified: { type: 'compositefile', value: fileSources },
            context: action.context,
        });
        // Update querySources in context: remove individual file sources, add composite source
        const fileSourceSet = new Set(fileSources);
        const newQuerySources = [
            ...querySources.filter(s => !fileSourceSet.has(s)),
            compositeWrapper,
        ];
        return {
            operation: action.operation,
            context: action.context.set(context_entries_1.KeysQueryOperation.querySources, newQuerySources),
        };
    }
    async getFilterFactorSafe(sourceWrapper, context) {
        const firstLink = sourceWrapper.source.firstLink;
        // Only hypermedia sources can represent files.
        // If the source has a forced file type, then we know it's a file,
        // otherwise, we need to invoke getFilterFactor (which is more expensive due to the HTTP lookup).
        if (firstLink === undefined) {
            return undefined;
        }
        if (firstLink.forceSourceType === 'file') {
            return 0;
        }
        return sourceWrapper.source.getFilterFactor(context);
    }
}
exports.ActorOptimizeQueryOperationGroupFileSources = ActorOptimizeQueryOperationGroupFileSources;
//# sourceMappingURL=ActorOptimizeQueryOperationGroupFileSources.js.map